import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { deepCopy, each } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import cs from 'classnames';
import styles from '../index.module.less';
import { Col, Form, Row } from 'antd';
import { easyProvinceChange, areaList, changeProvincesCities } from '../../../config-v2';
import V2Title from '../../../Feedback/V2Title';
import V2Form from '../../../Form/V2Form';
import V2FormSelect from '../../../Form/V2FormSelect/V2FormSelect';
import V2FormTreeSelect from '../../../Form/V2FormTreeSelect/V2FormTreeSelect';
import V2Container from '../../V2Container';

const V2KeyInProvincial: React.FC<any> = forwardRef(({
  valueKey,
  setOpen,
  setCheckedValue,
  extraCities,
  onConfirm,
  allowSpecialRegion,
  checkStrictly,
  onDataLoaded,
  beforeCheck,
}, ref) => {
  const dispatch = useDispatch();
  const province = useSelector((state: any) => state.common.provincesCities);
  const contentRef: any = useRef(null);
  const cacheCity: any = useRef();
  const [form] = Form.useForm();
  const [mainHeight, setMainHeight] = useState<number>(0);
  const [groups, setGroups] = useState<any[]>([]);
  const [list, setList] = useState<any[]>([]); // 组装后的城市数据
  const [treeList, setTreeList] = useState<any[]>([]); // 组装后的省市数据
  const [activeKey, setActiveKey] = useState<string | number>();
  const [newValue, setNewValue] = useState<any>({});
  const methods = useMethods({
    contentRefCb(el) {
      if (!contentRef.current) {
        contentRef.current = el;
        methods.contentRefScrollTo(el, newValue);
      }
    },
    contentRefScrollTo(el, checkedValue) {
      setTimeout(() => {
        if (checkedValue?.pinyin && checkedValue?.initial) {
          methods.onKeyClick(checkedValue.pinyin, checkedValue.initial, true);
        } else {
          el.scrollTop = 0;
        }
      }, 0);
    },
    getSelections(list: any[]) {
      const cityMap: any = {};
      const groups: any[] = [];
      const cityList: any[] = [];
      const newTreeList = deepCopy(list).map(item => {
        if (item.name.trim() && item.children?.length) {
          const key = item.pinyin.slice(0, 1).toUpperCase();
          if (!cityMap[key]) {
            cityMap[key] = [];
          }
          if (['台湾', '香港', '澳门'].some((region) => item.name.indexOf(region) !== -1)) {
            item.disabled = !allowSpecialRegion;
            item.children?.forEach(city => {
              city.disabled = !allowSpecialRegion;
            });
          }
          item.name = easyProvinceChange[item.name];
          item.initial = key;
          item.children = item.children.map(city => {
            city.key = `city-${key}-${item.id}-${city.id}`;
            const newCity = {
              ...city,
              pinyin: item.pinyin,
              initial: key,
              parent: {
                id: item.id,
                name: item.name,
                code: item.code,
              }
            };
            cityList.push(newCity); // 市级别数据收集
            return newCity;
          });
          cityMap[key].push(item); // 省级别数据收集

          // tree组件要求key值区分
          item.key = `province-${key}-${item.id}`;
        }
        item.isProvince = true;
        return item;
      });
      each(cityMap, (children: any, key: string) => {
        groups.push({
          index: key,
          title: key,
          children
        });
      });
      groups.sort((a, b) => a.index.toLowerCase().localeCompare(b.index.toLowerCase()));
      unstable_batchedUpdates(() => {
        cacheCity.current = cityMap;
        setGroups(groups);
        setList(cityList); // 组装市数据
        setTreeList(newTreeList); // 组装省市数据
        onDataLoaded && onDataLoaded(cityList, newTreeList);
      });
    },
    async getAreaList() {
      const cityResult: any = await areaList({ type: 2 });
      dispatch(changeProvincesCities(cityResult));
      methods.getSelections(cityResult);
    },
    onKeyClick(pinyin, initial, forProvince = false) {
      setActiveKey(initial);
      const parent = contentRef.current;
      let child;
      if (forProvince) {
        child = document.getElementsByClassName(`v2CityItemMainPY-${pinyin}`)[0];
      } else {
        child = document.getElementsByClassName(`v2CityItemPY-${initial}`)[0];
      }
      if (child && parent) {
        const parentTop = parent.getBoundingClientRect().top;
        const childTop = child.getBoundingClientRect().top;
        parent.scrollTop = childTop - parentTop + parent.scrollTop;
      }
    },
    async updateRes(item) {
      if (beforeCheck) {
        const stop = await beforeCheck(item);
        if (stop) {
          return;
        }
      }
      setOpen(false);
      setCheckedValue(item);
      onConfirm?.(item);
    },
    onChecked(item) {
      if (!item.disabled) {
        methods.updateRes(item);
      }
    },
    onProvinceClick(item) {
      if (checkStrictly) { // 是否允许选省
        if (!item.disabled) {
          methods.updateRes(item);
        }
      }
    },
    onSearch(value, item) {
      let _item = item;
      if (checkStrictly) {
        if (value) {
          const values = value.split('-');
          const isProvince = values[0] === 'province';
          const targetProvinceList = cacheCity.current[values[1]];
          if (isProvince) {
            const id = +values[2];
            _item = targetProvinceList.find(item => item.id === id);
            _item.isProvince = true;
          } else {
            const provinceId = +values[2];
            const cityId = +values[3];
            const targetProvince = targetProvinceList.find(item => item.id === provinceId);
            if (targetProvince.children?.length) {
              _item = targetProvince.children.find(item => item.id === cityId);
            }
          }
        }
      }

      setTimeout(() => {
        methods.updateRes(_item);
      }, 200);
    },
  });
  useEffect(() => {
    // 如果不存在则进行请求
    if (!province.length) {
      methods.getAreaList();
    } else {
      methods.getSelections(province);
    }
  }, []);
  useImperativeHandle(ref, () => ({
    async init(checkedValue) {
      setActiveKey(undefined);
      form.setFieldValue('searchCity', undefined);
      setNewValue(checkedValue);
      if (contentRef.current) { // 初次打开时，不会进入到这里，所以通过 contentRefCb触发
        methods.contentRefScrollTo(contentRef.current, checkedValue);
      }
    }
  }));
  return (
    <V2Container
      className={cs(styles.v2CitySearch, 'v2CitySearch')}
      style={{ height: `calc(100vh - 266px)` }}
      emitMainHeight={(h) => setMainHeight(h)} // 再生成一个新的高度，给table使用
      extraContent={{
        top: <>
          <V2Form form={form}>
            {
              checkStrictly ? (
                <V2FormTreeSelect
                  placeholder='搜索省市'
                  name='searchCity'
                  treeData={treeList}
                  config={{ showSearch: true, fieldNames: { label: 'name', value: 'key' }, allowClear: false, treeNodeFilterProp: 'name' }}
                  onChange={methods.onSearch}
                />
              ) : (
                <V2FormSelect
                  placeholder='搜索城市'
                  name='searchCity'
                  options={list}
                  config={{ showSearch: true, fieldNames: { label: 'name', value: valueKey }, allowClear: false }}
                  onChange={methods.onSearch}
                />
              )
            }
          </V2Form>
          {
            extraCities?.map((extra, index) => {
              return (
                <div key={index}>
                  <V2Title className={cs(styles.v2CityIndexBarTitle, 'v2CityIndexBarTitle')} type='H3' text={extra.title} extra={extra.extra}/>
                  <Row className={cs(styles.v2CityIndexBarRow, 'v2CityIndexBarRow')} gutter={[8, 8]}>
                    {
                      extra.cities.map((item, key) => {
                        return (
                          <Col
                            span={4}
                            key={key}
                            className={cs(styles.v2CityIndexBarCol, 'v2CityIndexBarCol')}
                            onClick={() => methods.onChecked(item)}
                          >
                            <div className={cs([
                              styles.v2CityIndexBarItem,
                              'v2CityIndexBarItem',
                              !newValue.isProvince && newValue.id === item.id && styles.active,
                            ])}
                            >
                              {item.name}
                            </div>
                          </Col>
                        );
                      })
                    }
                  </Row>
                </div>
              );
            })
          }
          <V2Title className={cs(styles.v2CityIndexBarTitle, 'v2CityIndexBarTitle')} type='H3' text='按字母选择'/>
          <div className={cs(styles.v2EnNameList, 'v2EnNameList')}>
            {
              groups.map(item => {
                return (<div
                  key={item.index}
                  className={cs([
                    styles.v2EnNameItem,
                    'v2EnNameItem',
                    activeKey === item.index && styles.active
                  ])}
                  onClick={() => methods.onKeyClick(item.pinyin, item.index)}
                >{item.title}</div>);
              })
            }
          </div>
        </>,
      }}
    >
      <div
        ref={methods.contentRefCb}
        className={cs([
          styles.v2CityContent,
          'v2CityContent',
          checkStrictly && styles.v2CitySearchCheckStrictly
        ])}
        style={{ maxHeight: mainHeight }}
      >
        {
          groups.map(item => {
            return (
              <div
                className={cs([
                  styles.v2CityItem,
                  'v2CityItem',
                  activeKey === item.index && styles.active,
                  `v2CityItemPY-${item.index}`
                ])}
                key={item.index}
              >
                <div className={cs(styles.v2CityItemKey, 'v2CityItemKey')}>
                  <div className={cs(styles.v2CityItemKeyInner, 'v2CityItemKeyInner')}>
                    <div className={cs(styles.v2CityItemKeyCover, 'v2CityItemKeyCover')}>
                      {item.title}
                    </div>
                    <div className={cs(styles.v2CityItemKeyCover2, 'v2CityItemKeyCover2')}></div>
                  </div>
                </div>
                {
                  item.children.map(province => {
                    return (
                      <div
                        className={cs([
                          styles.v2CityItemMain,
                          'v2CityItemMain',
                          `v2CityItemMainPY-${province.pinyin}`
                        ])}
                        key={province.id}
                      >
                        <div
                          onClick={
                            () => methods.onProvinceClick({
                              ...province,
                              initial: item.index,
                              isProvince: true
                            })
                          }
                          className={cs([
                            styles.v2CityItemMainLeft,
                            'v2CityItemMainLeft',
                            newValue.isProvince && newValue.id === province.id && styles.active,
                            province.disabled && styles.disabled,
                          ])}
                        >
                          {province.name}
                        </div>
                        <div className={cs(styles.v2CityItemMainRight, 'v2CityItemMainRight')}>
                          <div className={cs(styles.v2CityItemRightCities, 'v2CityItemRightCities')}>
                            {
                              province.children.map(city => {
                                return (
                                  <div
                                    key={city.id}
                                    className={cs([
                                      styles.v2CityItemRightCity,
                                      city.disabled && styles.disabled,
                                      'v2CityItemRightCity',
                                      !newValue.isProvince && newValue.id === city.id && styles.active
                                    ])}
                                    onClick={() => methods.onChecked({
                                      ...city,
                                      pinyin: province.pinyin,
                                      initial: item.index,
                                    })}
                                  >
                                    {city.name}
                                  </div>
                                );
                              })
                            }
                          </div>
                        </div>
                      </div>
                    );
                  })
                }
              </div>
            );
          })
        }
      </div>
    </V2Container>
  );
});

export default V2KeyInProvincial;
